import React from "react"
import DOMPurify from "dompurify"

/**
 * Parses text content and converts URLs to clickable links
 * @param text The text content to parse
 * @returns React elements with clickable links
 */
export function parseTextWithLinks(text: string): React.ReactNode {
  if (!text) return text

  // Regular expression to match URLs
  // This regex matches common URL patterns starting with http://, https://, or www.
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g

  // If no URLs found, return the original text
  const urls = text.match(urlRegex)
  if (!urls || urls.length === 0) return text

  // Build the result with React elements
  const result: React.ReactNode[] = []
  let lastIndex = 0

  // Use exec to get the indices of matches
  let match
  const regex = new RegExp(urlRegex)

  while ((match = regex.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index))
    }

    // Get the matched URL
    const url = match[0]

    // Ensure URL has proper protocol for href
    const href = url.startsWith("www.") ? `https://${url}` : url

    // Add the URL as a link
    result.push(
      React.createElement(
        "a",
        {
          key: `link-${match.index}`,
          href: href,
          target: "_blank",
          rel: "noopener noreferrer",
          className: "text-blue-600 hover:underline",
        },
        url,
      ),
    )

    // Update lastIndex to after this URL
    lastIndex = match.index + url.length
  }

  // Add any remaining text after the last URL
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex))
  }

  return result
}

/**
 * Parses HTML content and returns sanitized React elements
 * @param html The HTML content to parse
 * @returns Sanitized HTML content as a React element
 */
export function parseHtml(html: string): React.ReactNode {
  if (!html) return html

  // First sanitize the HTML to prevent XSS attacks
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "p", "br", "ul", "ol", "li", "span"],
    ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
  })

  // Return the sanitized HTML using React.createElement instead of JSX
  return React.createElement("div", {
    dangerouslySetInnerHTML: { __html: sanitizedHtml },
  })
}

/**
 * Detects if content contains HTML tags
 * @param content The content to check
 * @returns True if content contains HTML tags
 */
function containsHtmlTags(content: string): boolean {
  const htmlTagRegex = /<\/?[a-z][\s\S]*>/i
  return htmlTagRegex.test(content)
}

/**
 * Enhanced version that can parse both links and basic HTML tags
 * @param content The text content to parse
 * @returns React elements with parsed content
 */
export function parseContent(content: string): React.ReactNode {
  if (!content) return content

  // If content contains HTML tags, parse it as HTML
  if (containsHtmlTags(content)) {
    return parseHtml(content)
  }

  // Otherwise, just parse links
  return parseTextWithLinks(content)
}
